/**
 * SurvivalWaveGenerator — pure, no Phaser import (node-testable).
 *
 * Given wave number N, deterministically produces a WaveDef + boss-milestone
 * data for Survival mode. No randomness — same N always yields the same wave.
 *
 * Escalation rules:
 *   - Enemy count grows 0.5 per wave, capped at SURVIVAL_MAX_ENEMIES.
 *   - Tier mix lerps from all-galsia (wave 1) to a balanced elite mix (wave 30+).
 *   - Every SURVIVAL_ELITE_INTERVAL-th wave (5, 15, 25…) → elite-heavy weights.
 *   - Every SURVIVAL_BOSS_INTERVAL-th wave (10, 20, 30…) → boss milestone.
 *   - Boss waves halve the regular enemy count (boss is the main event).
 *
 * House style: ComboTracker / BossRushSequence pattern — framework-free,
 * injectable pool for tests, deterministic via wave-seeded slot picks.
 */

import type { WaveDef, EnemyGroup } from '@/data/stages/stageConfig';

// ── Types ─────────────────────────────────────────────────────────────────────

/** Per-tier archetype ID pools passed in by the caller (injectable for tests). */
export interface SurvivalEnemyPool {
  /** Tier 1 cannon fodder (galsia-equivalent). */
  galsia: readonly string[];
  /** Tier 2 mid-range (donovan-equivalent). */
  donovan: readonly string[];
  /** Tier 3 tough / handler (signal-equivalent). */
  signal: readonly string[];
  /** Mini-boss / elite tier — ignoresHitstun. */
  elite: readonly string[];
}

/** Result of generating one survival wave. */
export interface SurvivalWaveResult {
  /** Enemies to spawn for this wave. */
  wave: WaveDef;
  /**
   * Boss ID cycling from the boss roster on every SURVIVAL_BOSS_INTERVAL wave.
   * Null on non-boss waves.
   */
  bossId: string | null;
  /** True on elite-heavy milestone waves (every 5th, excluding boss waves). */
  isEliteWave: boolean;
}

// ── Constants ─────────────────────────────────────────────────────────────────

export const SURVIVAL_BASE_ENEMY_COUNT = 3;
export const SURVIVAL_COUNT_GROWTH = 0.5;
export const SURVIVAL_MAX_ENEMIES = 12;
export const SURVIVAL_BOSS_INTERVAL = 10;
export const SURVIVAL_ELITE_INTERVAL = 5;

// ── Pure helpers ──────────────────────────────────────────────────────────────

/** Deterministic enemy count for wave N (caps at SURVIVAL_MAX_ENEMIES). */
export function survivalEnemyCount(waveN: number): number {
  const raw = SURVIVAL_BASE_ENEMY_COUNT + Math.floor((waveN - 1) * SURVIVAL_COUNT_GROWTH);
  return Math.min(raw, SURVIVAL_MAX_ENEMIES);
}

/**
 * Tier weight distribution at wave N.
 * Linearly interpolates from all-galsia (wave 1) to a balanced mix (wave 30+).
 */
export function survivalTierWeights(waveN: number): {
  galsia: number;
  donovan: number;
  signal: number;
  elite: number;
} {
  const t = Math.min((waveN - 1) / 29, 1);
  return {
    galsia: lerp(1.0, 0.2, t),
    donovan: lerp(0.0, 0.3, t),
    signal: lerp(0.0, 0.3, t),
    elite: lerp(0.0, 0.2, t),
  };
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function eliteHeavyWeights(): { galsia: number; donovan: number; signal: number; elite: number } {
  return { galsia: 0.1, donovan: 0.2, signal: 0.3, elite: 0.4 };
}

/**
 * Deterministic pick from pool using wave number + slot as a seed.
 * Returns a stable element for the same (waveN, slot) pair.
 */
function pickDeterministic(pool: readonly string[], waveN: number, slot: number): string {
  if (pool.length === 0) return '';
  return pool[(waveN * 7 + slot * 13) % pool.length];
}

function buildEnemyGroups(
  waveN: number,
  totalCount: number,
  weights: { galsia: number; donovan: number; signal: number; elite: number },
  pool: SurvivalEnemyPool,
  isBossWave: boolean,
): EnemyGroup[] {
  const effectiveCount = isBossWave ? Math.max(1, Math.floor(totalCount / 2)) : totalCount;
  const groups = new Map<string, number>();

  function addTier(tierIds: readonly string[], weight: number): void {
    if (tierIds.length === 0 || weight <= 0) return;
    const n = Math.round(effectiveCount * weight);
    if (n <= 0) return;
    const id = pickDeterministic(tierIds, waveN, groups.size);
    groups.set(id, (groups.get(id) ?? 0) + n);
  }

  addTier(pool.galsia, weights.galsia);
  addTier(pool.donovan, weights.donovan);
  addTier(pool.signal, weights.signal);
  addTier(pool.elite, weights.elite);

  // Guarantee at least one enemy.
  if (groups.size === 0) {
    const fallback = pool.galsia[0] ?? pool.donovan[0] ?? pool.signal[0] ?? pool.elite[0];
    if (fallback) groups.set(fallback, Math.max(1, effectiveCount));
  }

  return Array.from(groups.entries()).map(([id, count]) => ({ id, count }));
}

// ── Main entry point ──────────────────────────────────────────────────────────

/**
 * Generate the survival WaveDef + milestone data for wave number N.
 *
 * @param waveN      1-indexed wave number.
 * @param pool       Enemy archetype IDs by tier (injectable; use SURVIVAL_ENEMY_POOL in prod).
 * @param bossRoster Ordered boss IDs cycled on every SURVIVAL_BOSS_INTERVAL wave.
 */
export function generateSurvivalWave(
  waveN: number,
  pool: SurvivalEnemyPool,
  bossRoster: readonly string[],
): SurvivalWaveResult {
  const isBossWave = waveN % SURVIVAL_BOSS_INTERVAL === 0;
  const isEliteWave = !isBossWave && waveN % SURVIVAL_ELITE_INTERVAL === 0;

  const count = survivalEnemyCount(waveN);
  const weights = isEliteWave ? eliteHeavyWeights() : survivalTierWeights(waveN);
  const enemies = buildEnemyGroups(waveN, count, weights, pool, isBossWave);

  let bossId: string | null = null;
  if (isBossWave && bossRoster.length > 0) {
    const bossIdx = Math.floor(waveN / SURVIVAL_BOSS_INTERVAL - 1) % bossRoster.length;
    bossId = bossRoster[bossIdx];
  }

  return { wave: { enemies }, bossId, isEliteWave };
}
